import { isSimpleIoSensorType, isValueMapSensorType } from "../domain";
import type {
  RfidConfig,
  SensorAssignmentListItem,
  SensorCreateInput,
  SensorCreateResultData,
  SensorDetail,
  SensorFormField,
  SensorFormFieldValidation,
  SensorFormMeta,
  SensorIoConflictEntry,
  SensorUpdateInput,
  SimpleIoConfig,
  TemperatureConfig,
  ValueMapConfig,
  WeightConfig,
} from "../domain";
import type {
  RfidConfigDto,
  SensorAssignmentListItemDto,
  SensorCreateInputDto,
  SensorCreateResultDataDto,
  SensorDetailDto,
  SensorFormMetaDto,
  SensorIoConflictEntryDto,
  SensorUpdateInputDto,
  SimpleIoConfigDto,
  TemperatureConfigDto,
  ValueMapConfigDto,
  WeightConfigDto,
} from "./sensorAssignmentDtos";

// zod's `.optional()` types a field as `T | undefined`, which under
// `exactOptionalPropertyTypes` isn't assignable to a domain `field?: T`
// (that only allows the key to be *absent*, never explicitly `undefined`) --
// so each optional field is copied over only when actually set.
function sensorFormFieldValidationFromDto(
  dto: SensorFormMetaDto["fields"][number]["validation"],
): SensorFormFieldValidation {
  return {
    ...(dto.min === undefined ? {} : { min: dto.min }),
    ...(dto.max === undefined ? {} : { max: dto.max }),
    ...(dto.minLength === undefined ? {} : { minLength: dto.minLength }),
    ...(dto.maxLength === undefined ? {} : { maxLength: dto.maxLength }),
    ...(dto.pattern === undefined ? {} : { pattern: dto.pattern }),
    ...(dto.patternFlags === undefined ? {} : { patternFlags: dto.patternFlags }),
  };
}

function sensorFormFieldFromDto(
  dto: SensorFormMetaDto["fields"][number],
): SensorFormField {
  return {
    key: dto.key,
    type: dto.type,
    labelEn: dto.labelEn,
    labelAr: dto.labelAr,
    requiredIn: dto.requiredIn,
    forbiddenIn: dto.forbiddenIn,
    ...(dto.optionGroup === undefined ? {} : { optionGroup: dto.optionGroup }),
    validation: sensorFormFieldValidationFromDto(dto.validation),
    sortOrder: dto.sortOrder,
  };
}

export function sensorAssignmentListItemFromDto(
  dto: SensorAssignmentListItemDto,
): SensorAssignmentListItem {
  return {
    id: dto.id,
    sensorType: dto.sensorType,
    name: dto.name,
    configType: dto.configType,
    minValue: dto.minValue,
    maxValue: dto.maxValue,
    unit: dto.unit,
    tagLength: dto.tagLength,
    sensorConfig: dto.sensorConfig,
    device: dto.device,
    account: dto.account,
    createdAt: dto.createdAt,
  };
}

export function sensorFormMetaFromDto(dto: SensorFormMetaDto): SensorFormMeta {
  return {
    sensorType: dto.sensorType,
    variantKey: dto.variantKey,
    variants: dto.variants,
    options: dto.options,
    fields: dto.fields.map(sensorFormFieldFromDto),
    rules: dto.rules,
  };
}

function weightConfigToDto(config: WeightConfig): WeightConfigDto {
  return {
    label: config.label,
    unitLabel: config.unitLabel,
    formulaType: config.formulaType,
    ios: config.ios.map((io) => ({
      io: io.io,
      ...(io.label === undefined ? {} : { label: io.label }),
      minWeightKGs: io.minWeightKGs,
      maxWeightKGs: io.maxWeightKGs,
      calibration: io.calibration.map((point) => ({ ...point })),
      ioFormulaPercentage: io.ioFormulaPercentage,
    })),
  };
}

function simpleIoConfigToDto(config: SimpleIoConfig): SimpleIoConfigDto {
  return {
    label: config.label,
    ios: config.ios.map((io) => ({ io: io.io })),
  };
}

function rfidConfigToDto(config: RfidConfig): RfidConfigDto {
  return {
    label: config.label,
    ios: config.ios.map((io) => ({ io: io.io, tagLength: io.tagLength })),
  };
}

function valueMapConfigToDto(config: ValueMapConfig): ValueMapConfigDto {
  return {
    label: config.label,
    ios: config.ios.map((io) => ({
      label: io.label,
      io: io.io,
      values: io.values.map((value) => ({
        value: value.value,
        label: { en: value.labelEn, ar: value.labelAr },
      })),
    })),
  };
}

function temperatureConfigToDto(config: TemperatureConfig): TemperatureConfigDto {
  return {
    label: config.label,
    ios: config.ios.map((io) => ({
      label: io.label,
      probeType: io.probeType,
      beaconModel: io.beaconModel,
      temperature: { ...io.temperature },
      ...(io.humidity === undefined ? {} : { humidity: { ...io.humidity } }),
      ...(io.battery === undefined ? {} : { battery: { ...io.battery } }),
      ...(io.dallasId === undefined ? {} : { dallasId: { ...io.dallasId } }),
    })),
  };
}

// The real `POST /sensors` body is flat `deviceIds` -- no "all"/branch
// concept exists on the wire (see SensorTargetScope). `ALL`/`BRANCH` targets
// only make sense against the mock repository, which resolves them itself;
// asking the HTTP repository to send one is a programming error, not a
// recoverable API response, so this throws rather than guess at a shape.
export function sensorCreateInputToDto(
  input: SensorCreateInput,
): SensorCreateInputDto {
  if (input.target.type !== "DEVICES") {
    throw new Error(
      `POST /sensors only accepts a DEVICES target today -- ${input.target.type} scopes aren't supported by the live API yet (see SensorTargetScope)`,
    );
  }
  const deviceIds = [...input.target.deviceIds];
  if ("sensorConfigId" in input) {
    return { deviceIds, sensorConfigId: input.sensorConfigId };
  }
  if (input.sensorType === "WEIGHT") {
    return { deviceIds, sensorType: "WEIGHT", config: weightConfigToDto(input.config) };
  }
  if (input.sensorType === "TEMPERATURE") {
    return {
      deviceIds,
      sensorType: "TEMPERATURE",
      config: temperatureConfigToDto(input.config),
    };
  }
  if (input.sensorType === "RFID") {
    return { deviceIds, sensorType: "RFID", config: rfidConfigToDto(input.config) };
  }
  // A custom type-guard call on a nested property (isSimpleIoSensorType,
  // isValueMapSensorType) narrows that property's own type but not this
  // discriminated union's `input` itself -- TS only narrows a union through
  // a *direct* literal equality/`in`/`typeof` check on the discriminant, the
  // same as the WEIGHT/TEMPERATURE/RFID checks above. SimpleIoSensorType is
  // currently just "IBUTTON" -- checking it by name lets TS narrow `input`
  // by elimination; ValueMap is then the exhaustive fallback for whatever's
  // left. (If SimpleIoSensorType ever grows past one member, this needs its
  // own explicit literal per member, same as WEIGHT/TEMPERATURE/RFID.)
  if (input.sensorType === "IBUTTON") {
    return {
      deviceIds,
      sensorType: "IBUTTON",
      config: simpleIoConfigToDto(input.config),
    };
  }
  return {
    deviceIds,
    sensorType: input.sensorType,
    config: valueMapConfigToDto(input.config),
  };
}

export function sensorUpdateInputToDto(
  input: SensorUpdateInput,
): SensorUpdateInputDto {
  if (input.sensorType === "WEIGHT") {
    return { config: weightConfigToDto(input.config) };
  }
  if (input.sensorType === "TEMPERATURE") {
    return { config: temperatureConfigToDto(input.config) };
  }
  if (input.sensorType === "RFID") {
    return { config: rfidConfigToDto(input.config) };
  }
  // See the matching comment in sensorCreateInputToDto -- narrowing by
  // elimination needs a direct literal check, not a predicate call.
  if (input.sensorType === "IBUTTON") {
    return { config: simpleIoConfigToDto(input.config) };
  }
  return { config: valueMapConfigToDto(input.config) };
}

function weightConfigFromDto(dto: WeightConfigDto): WeightConfig {
  return {
    label: dto.label,
    unitLabel: dto.unitLabel,
    formulaType: dto.formulaType,
    ios: dto.ios.map((io) => ({
      io: io.io,
      ...(io.label === undefined ? {} : { label: io.label }),
      minWeightKGs: io.minWeightKGs,
      maxWeightKGs: io.maxWeightKGs,
      calibration: io.calibration.map((point) => ({ ...point })),
      ioFormulaPercentage: io.ioFormulaPercentage,
    })),
  };
}

function temperatureChannelFromDto(dto: {
  io: number;
  unitLabel?: string | undefined;
}): { io: number; unitLabel?: string } {
  return {
    io: dto.io,
    ...(dto.unitLabel === undefined ? {} : { unitLabel: dto.unitLabel }),
  };
}

function temperatureConfigFromDto(dto: TemperatureConfigDto): TemperatureConfig {
  return {
    label: dto.label,
    ios: dto.ios.map((io) => ({
      label: io.label,
      probeType: io.probeType,
      beaconModel: io.beaconModel,
      temperature: temperatureChannelFromDto(io.temperature),
      ...(io.humidity === undefined
        ? {}
        : { humidity: temperatureChannelFromDto(io.humidity) }),
      ...(io.battery === undefined
        ? {}
        : { battery: temperatureChannelFromDto(io.battery) }),
      ...(io.dallasId === undefined ? {} : { dallasId: { ...io.dallasId } }),
    })),
  };
}

function simpleIoConfigFromDto(dto: SimpleIoConfigDto): SimpleIoConfig {
  return {
    label: dto.label,
    ios: dto.ios.map((io) => ({ io: io.io })),
  };
}

function rfidConfigFromDto(dto: RfidConfigDto): RfidConfig {
  return {
    label: dto.label,
    ios: dto.ios.map((io) => ({ io: io.io, tagLength: io.tagLength })),
  };
}

function valueMapConfigFromDto(dto: ValueMapConfigDto): ValueMapConfig {
  return {
    label: dto.label,
    ios: dto.ios.map((io) => ({
      label: io.label,
      io: io.io,
      values: io.values.map((value) => ({
        value: value.value,
        labelEn: value.label.en,
        labelAr: value.label.ar,
      })),
    })),
  };
}

// `dto.config`'s shape is only disambiguated by the sibling `sensorType`
// field -- the wire union itself carries no discriminant tag.
function sensorConfigFromDto(
  dto: Pick<SensorDetailDto, "sensorType" | "config">,
): SensorDetail["config"] {
  if (dto.config === null) return null;
  if (dto.sensorType === "WEIGHT") {
    return weightConfigFromDto(dto.config as WeightConfigDto);
  }
  if (dto.sensorType === "TEMPERATURE") {
    return temperatureConfigFromDto(dto.config as TemperatureConfigDto);
  }
  if (dto.sensorType === "RFID") {
    return rfidConfigFromDto(dto.config as RfidConfigDto);
  }
  if (isValueMapSensorType(dto.sensorType)) {
    return valueMapConfigFromDto(dto.config as ValueMapConfigDto);
  }
  if (isSimpleIoSensorType(dto.sensorType)) {
    return simpleIoConfigFromDto(dto.config as SimpleIoConfigDto);
  }
  return null;
}

/** `GET /sensors/:sensorId` and `GET /devices/:deviceId/sensors` -- both confirmed real, same row shape. */
export function sensorDetailFromDto(dto: SensorDetailDto): SensorDetail {
  return {
    ...sensorAssignmentListItemFromDto(dto),
    config: sensorConfigFromDto(dto),
    updatedAt: dto.updatedAt,
  };
}

export function sensorCreateResultDataFromDto(
  dto: SensorCreateResultDataDto,
): SensorCreateResultData {
  return {
    sensorConfigId: dto.sensorConfigId,
    created: dto.created.map(sensorDetailFromDto),
    updated: dto.updated.map(sensorDetailFromDto),
  };
}

export function sensorIoConflictEntryFromDto(
  dto: SensorIoConflictEntryDto,
): SensorIoConflictEntry {
  return { deviceId: dto.deviceId, imei: dto.imei, io: dto.io };
}
